import { privateRequest } from "../axios";
import type { ApiResponse, ExportFileResult, ListApiResponse, ListData } from "@/types/common";
import { parseFilenameFromContentDisposition } from "@/lib/downloadExcel";
import type { Event } from "./events";
import type { UserType } from "@/types/question";

// Confirmed against a real authenticated `GET /users` response (decision
// #18/#19 in docs/decisions.md) — this is the actual shape, not a guess.
export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  mobileNo: string | null;
  location: string | null;
  profileUrl: string | null;
  isActive: boolean;
  companyName: string | null;
  companyWebsite: string | null;
  designation: string | null;
  gender: string | null;
  dob: string | null;
  status: string;
  statusRemark: string | null;
  userType: string;
  userCategory?: UserCategory | string | null;
  socialLink: { instagramProfileUrl: string | null; linkedinProfileUrl: string | null } | null;
  userSettings: { isPushNotificationEnabled: boolean; language: string } | null;
  eventId: number | string | null;
  latestRating: number | null;
  latestReview: string | null;
  createdAt: string;
  cityName: string | null;
  currentFocus: string | null;
  fundingRound: string | null;
  sector: string | null;
  helpTopics: string[] | null;
  needTopics: string[] | null;
  engagementLevel: string | null;
  lastActiveAt: string | null;
  userRoles?: { id: number; name: string; description: string | null; isDefault: boolean }[] | null;
}

export interface UsersListData extends ListData<User> {
  approvedCount: number;
  subscriptionRenewalsCount: number;
}

export interface GetUsersParams {
  search?: string | undefined;
  pageNo?: number;
  perPage?: number;
  usertype?: string | undefined;
  userType?: string | undefined;
  screenType?: string | undefined;
  status?: string | undefined;
  role?: string | undefined;
  isExport?: boolean | undefined;
  fromDate?: string | undefined;
  toDate?: string | undefined;
  renewalDays?: number | undefined;
}

// Real backend enums, given directly by the user (`UserStatus`/
// `UserCategory` Java enums). "BLOCK" — not "BLOCKED" as `Members.tsx`'s
// status filter previously guessed; that filter is corrected to match.
export type UserStatus = "UNDER_REVIEW" | "APPROVED" | "REJECTED" | "BLOCK";
export type UserCategory = "CHARTERED" | "UNCHARTERED";

// Select options for `UserCategory` — shared by AddMemberModal and
// CreatePlanModal so the labels/values stay identical in both.
export const USER_CATEGORY_OPTIONS: { label: string; value: UserCategory }[] = [
  { label: "Chartered", value: "CHARTERED" },
  { label: "Unchartered", value: "UNCHARTERED" },
];

// Role (`userType`) filter options for the Members and Waitlist lists —
// values match `UserType` in types/question.ts, sent as `usertype`.
export const USER_TYPE_OPTIONS: { label: string; value: UserType }[] = [
  { label: "Founder", value: "FOUNDER" },
  { label: "Investor", value: "INVESTOR" },
];

export interface InviteAdminPayload {
  firstName: string;
  lastName: string;
  email: string;
  roleIds: number[];
  mobileNo?: string;
}
export interface UpdateAdminPayload {
  firstName: string;
  lastName: string;
  mobileNo: string;
  roleIds: number[];
}
export interface CreateUserPayload {
  firstName: string;
  lastName: string;
  email: string;
  mobileNo: string;
  chapterId: number;
  subscriptionPlanId: number;
  startsAt: string;
  companyName?: string;
  userType: UserType;
  userCategory: UserCategory;
}

// Real endpoint given directly by the user: PATCH /admin/users/{id}/status
// — used by ApplicationReview.tsx's "Approve & Send Invite"/"Decline
// Application" actions to move a waitlisted user's status.
export interface UpdateUserStatusPayload {
  status: UserStatus;
  statusRemark: string;
  userCategory: UserCategory;
}

// Real endpoint, given directly by the user: `GET /users/{id}/answers`.
// Two real sample responses disagree on the field name inside `answers`:
// one has `{ value: "..." }`, the other has `{ answer: "...",
// questionOptionId?: number }` (the latter only present on option-backed
// questions, e.g. a select/radio, not free text). Both fields are kept as
// optional and read defensively (see ApplicationReview.tsx) rather than
// picking one and guessing the other sample was wrong. Multi-select
// answers come back as a single comma-joined string (e.g. "Exploring
// ides,Building MVP,Early Transaction") rather than an array.
export interface UserAnswer {
  questionTitle: string;
  answers: { value?: string; answer?: string; questionOptionId?: number };
}

// GET /users/{id}/events/participated — endpoint path given directly by
// the user; unlike the rest of this file, no sample response was
// available to confirm the shape against. Modeled as the real `Event`
// shape (services/api/events.ts) plus a per-member `status` field, since a
// real Audit Log sample separately confirmed an "EventParticipant"
// module/entity exists on this backend (see docs/decisions.md #68) — a
// reasonable guess at what that join surfaces, not a confirmed response.
// `status`'s real enum values are unknown, so MemberDetails.tsx classifies
// it by keyword heuristic (`classifyAttendance`) rather than an exact
// lookup table, so an unanticipated real value still renders instead of
// falling through to nothing.
export interface ParticipatedEvent extends Event {
  status: string | null;
}

// Confirmed against a real `GET /users/{id}/subscription` response, given
// directly by the user — supersedes the earlier guessed shape (nested
// under a `SubscriptionPlan`-like `plan` object, with an `autoRenew`
// boolean and a `nextBillingDate`; see docs/decisions.md #69), which
// turned out wrong on every one of those points: this is a flat record,
// there's no auto-renew concept anywhere in it, and `endDate` — not a
// "next billing" date — is the closest real field to what the UI called
// "Renews" (see MemberDetails.tsx's relabel to "Expires"). `firstName`/
// `lastName` here are the *subscriber's* name as recorded on the
// subscription/payment record — redundant with the `User` MemberDetails.tsx
// already loads separately, not a reason to prefer this copy over that
// one. `queuedSubscription` was `null` and `planQueued` was `false` in the
// only sample seen — `queuedSubscription`'s populated shape isn't known,
// so it's kept as `unknown` and not rendered anywhere yet.
export interface MemberSubscription {
  planId: number;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  status: string;
  transactionId: string | null;
  taxAmount: number;
  totalAmount: number;
  amount: number;
  firstName: string | null;
  lastName: string | null;
  queuedSubscription: unknown;
  invoiceUrl: string | null;
  durationMonth: number;
  paymentType: string | null;
  planQueued: boolean;
}

const ENDPOINTS = {
  USERS: "/users",
  ADMIN_USERS: "/admin-users",
};

const normalizeParams = (params?: GetUsersParams) => {
  if (!params) return undefined;
  const queryParams = { ...params };
  if (queryParams.userType && !queryParams.usertype) {
    queryParams.usertype = queryParams.userType;
    delete queryParams.userType;
  }
  return queryParams;
};

const getUsers = async (params?: GetUsersParams) => {
  const queryParams = normalizeParams(params);
  const endpoint = queryParams?.role === "ADMIN" ? ENDPOINTS.ADMIN_USERS : ENDPOINTS.USERS;
  const response = await privateRequest.get<ApiResponse<UsersListData>>(endpoint, { params: queryParams });
  return response.data;
};

const getAdminUsers = async (params?: GetUsersParams) => {
  const response = await privateRequest.get<ApiResponse<UsersListData>>(ENDPOINTS.ADMIN_USERS, {
    params: normalizeParams(params),
  });
  return response.data;
};

// Same `isExport: true`-returns-a-file convention as
// `services/api/auditLog.ts`'s `exportActionLogs` — see that function's
// comment and docs/decisions.md #74. Used by Members.tsx, Waitlist.tsx,
// and Ticketing.tsx's export flows (all three read this same `/users`
// endpoint for their tables).
const exportUsers = async (params?: Omit<GetUsersParams, "isExport">): Promise<ExportFileResult> => {
  const queryParams = normalizeParams(params);
  const endpoint = queryParams?.role === "ADMIN" ? ENDPOINTS.ADMIN_USERS : ENDPOINTS.USERS;
  const response = await privateRequest.get(endpoint, {
    params: { ...queryParams, isExport: true },
    responseType: "blob",
  });
  return {
    blob: response.data as Blob,
    fileName: parseFilenameFromContentDisposition(response.headers["content-disposition"]),
  };
};

const updateUserStatus = async (userId: number, payload: UpdateUserStatusPayload) => {
  const response = await privateRequest.patch(`${ENDPOINTS.USERS}/${userId}/status`, payload);
  return response.data;
};

const blockUser = async (userId: number) => {
  const response = await privateRequest.patch(`${ENDPOINTS.USERS}/${userId}/status`, { status: "BLOCK" });
  return response.data;
};

const deleteUser = async (userId: number) => {
  const response = await privateRequest.delete<ApiResponse<unknown>>(`${ENDPOINTS.ADMIN_USERS}/${userId}`);
  return response.data;
};

const createUser = async (payload: CreateUserPayload) => {
  const response = await privateRequest.post(ENDPOINTS.USERS, payload);
  return response.data;
};

const inviteAdmin = async (payload: InviteAdminPayload) => {
  const response = await privateRequest.post(ENDPOINTS.ADMIN_USERS, payload);
  return response.data;
};

const updateAdmin = async (adminId: number, payload: UpdateAdminPayload) => {
  const response = await privateRequest.put(
    `${ENDPOINTS.ADMIN_USERS}/${adminId}`,
    payload
  );

  return response.data;
};
const getUserAnswers = async (userId: number) => {
  const response = await privateRequest.get<ApiResponse<UserAnswer[]>>(`${ENDPOINTS.USERS}/${userId}/answers`);
  return response.data;
};

const getParticipatedEvents = async (userId: number) => {
  const response = await privateRequest.get<ListApiResponse<ParticipatedEvent>>(
    `${ENDPOINTS.USERS}/${userId}/events/participated`,
  );
  return response.data;
};

const getMemberSubscription = async (userId: number) => {
  const response = await privateRequest.get<ApiResponse<MemberSubscription | null>>(
    `${ENDPOINTS.USERS}/${userId}/subscription`,
  );
  return response.data;
};

const userService = {
  getUsers,
  getAdminUsers,
  exportUsers,
  createUser,
  updateUserStatus,
  blockUser,
  deleteUser,
  deleteAdmin: deleteUser,
  inviteAdmin,
  getUserAnswers,
  getParticipatedEvents,
  getMemberSubscription,
  updateAdmin,
};

export default userService;
